// src/components/BulkMessagingPage.js
// Simplified for Template Support (Friendly UI)
import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import PageHeader from './common/PageHeader';
import {
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Grid,
    Divider,
    IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

export default function BulkMessagingPage() {
    const [numbers, setNumbers] = useState('');
    const [messageType] = useState('template'); // Forced to template

    // Template Mode State
    const [templateName, setTemplateName] = useState('');
    const [languageCode, setLanguageCode] = useState('ar');

    // Dynamic Template Variables
    const [templateVariables, setTemplateVariables] = useState([{ value: '' }]);

    const [headerImage, setHeaderImage] = useState(null);
    const [headerImageUrl, setHeaderImageUrl] = useState('');

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // Fetch Client Profile on Mount to get persisted template info
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axiosInstance.get('/chatbot/me/');
                const client = response.data.client;
                if (client) {
                    if (client.last_bulk_template_name) {
                        setTemplateName(client.last_bulk_template_name);
                    }
                    if (client.last_bulk_language_code) {
                        setLanguageCode(client.last_bulk_language_code);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch client profile", err);
            }
        };
        fetchProfile();
    }, []);

    // Add a new template variable field
    const handleAddVariable = () => {
        setTemplateVariables([...templateVariables, { value: '' }]);
    };

    // Remove a template variable field by index
    const handleRemoveVariable = (index) => {
        if (templateVariables.length > 1) {
            setTemplateVariables(templateVariables.filter((_, i) => i !== index));
        }
    };

    // Update variable value by index
    const handleVariableChange = (index, value) => {
        const updated = [...templateVariables];
        updated[index].value = value;
        setTemplateVariables(updated);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setHeaderImage(e.target.files[0]);
        }
    };

    const handleSend = async () => {
        setError(null);
        setResult(null);

        // Validation
        if (!numbers.trim()) {
            setError('يرجى إدخال أرقام الهواتف.');
            return;
        }

        if (!templateName.trim()) {
            setError('يرجى إدخال اسم القالب (Template Name).');
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('numbers', numbers);
            formData.append('type', messageType);
            formData.append('template_name', templateName);
            formData.append('language_code', languageCode);

            const variableValues = templateVariables.map(v => v.value);
            formData.append('template_variables', JSON.stringify(variableValues));

            if (headerImage) {
                formData.append('header_image', headerImage);
            } else if (headerImageUrl) {
                formData.append('header_image_url', headerImageUrl);
            }

            const response = await axiosInstance.post('/chatbot/broadcast/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setResult(response.data);

            // Re-fetch profile to update local state with saved template name (if needed)
            // Or just update local state if we want to be "locked" as per user request
            // The user said "ثبتو ميتغيرش تانى" - once entered it should stay. 
            // My backend saves it on success.
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'حدث خطأ أثناء الإرسال.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <PageHeader
                title="إرسال رسائل ترويجية جماعية"
                subtitle="أرسل رسائل لعملائك دفعة واحدة باستخدام القوالب المسجلة في واتساب"
            />
            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, maxWidth: 900 }}>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {result && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        {result.message} (تمت المعالجة: {result.total_processed})
                        {result.failed_count > 0 && ` - فشل: ${result.failed_count}`}
                    </Alert>
                )}

                <Box component="form" noValidate autoComplete="off">

                    {/* Phone Numbers Input */}
                    <TextField
                        label="أرقام الهواتف"
                        placeholder="مثال: 966500000000, 966511111111"
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={numbers}
                        onChange={(e) => setNumbers(e.target.value)}
                        sx={{ mb: 4 }}
                        helperText="أدخل الأرقام مع مفتاح الدولة، كل رقم في سطر جديد أو مفصولة بفواصل."
                    />

                    <Divider sx={{ mb: 4 }} />

                    <Box sx={{ animation: 'fadeIn 0.3s' }}>
                        <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fbfcfd' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Template Name"
                                        fullWidth
                                        required
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        helperText="الاسم البرمجي للقالب في واتساب (مثال: ramadan_offer)"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Language Code"
                                        fullWidth
                                        value={languageCode}
                                        onChange={(e) => setLanguageCode(e.target.value)}
                                        helperText="مثال: ar, en"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                                        <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold" sx={{ mb: 2 }}>
                                            مكونات القالب (Template Components)
                                        </Typography>

                                        {/* Header Image Upload */}
                                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                startIcon={<CloudUploadIcon />}
                                                size="medium"
                                                sx={{ borderRadius: 2 }}
                                            >
                                                رفع صورة الهيدر
                                                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                                            </Button>
                                            {headerImage ? (
                                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                    {headerImage.name}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">لم يتم اختيار ملف</Typography>
                                            )}
                                        </Box>

                                        {/* Header Image URL Fallback */}
                                        <TextField
                                            label="أو رابط صورة الهيدر (URL)"
                                            fullWidth
                                            size="small"
                                            value={headerImageUrl}
                                            onChange={(e) => setHeaderImageUrl(e.target.value)}
                                            placeholder="https://example.com/image.jpg"
                                            helperText="استخدم هذا إذا كانت الصورة مرفوعة أونلاين بالفعل."
                                            sx={{ mb: 3 }}
                                        />

                                        <Divider sx={{ my: 2, borderStyle: 'dotted' }} />

                                        {/* Dynamic Template Variables */}
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ mt: 1 }}>
                                                متغيرات القالب (Variables)
                                            </Typography>
                                            {templateVariables.map((variable, index) => (
                                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                                    <TextField
                                                        label={`المتغير ${index + 1}`}
                                                        fullWidth
                                                        size="small"
                                                        value={variable.value}
                                                        onChange={(e) => handleVariableChange(index, e.target.value)}
                                                        placeholder={`قيمة المتغير ${index + 1}`}
                                                    />
                                                    {templateVariables.length > 1 && (
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => handleRemoveVariable(index)}
                                                            size="small"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ))}
                                            <Button
                                                variant="text"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddVariable}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                إضافة متغير آخر
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleSend}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{ px: 6, py: 1.5, borderRadius: 2, fontSize: '1.1rem', fontWeight: 'bold', boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)' }}
                        >
                            {loading ? 'جاري المعالجة...' : 'إرسال الرسائل الآن'}
                        </Button>
                    </Box>

                </Box>
            </Paper>
        </Box>
    );
}
